import React, { useEffect, useState } from "react";
import { Box, MenuItem, Select, TextField, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import axios from "axios";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import {
  useEngagementTheme,
  engagementPageSx,
  EngagementCard,
  EngagementPill,
  engagementButtonSx,
  engagementChipSx,
} from "../components/engagementTheme";

const STATUS_CHIPS = ["Open", "In progress", "Closed", "All"];
const CATEGORIES = ["technical", "billing", "general", "other"];
const PRIORITIES = ["low", "medium", "high"];

const priorityTone = (p) => (p === "high" ? "crit" : p === "medium" ? "warn" : "mute");
const statusTone = (s) => (s === "closed" ? "ok" : s === "in-progress" ? "warn" : "info");

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return `${d.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}, ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false })}`;
};

const EngagementTickets = () => {
  const t = useEngagementTheme();
  const [tickets, setTickets] = useState([]);
  const [status, setStatus] = useState("Open");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(15);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [draft, setDraft] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [doctorQuery, setDoctorQuery] = useState("");
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [doctor, setDoctor] = useState(null);
  const [clinicQuery, setClinicQuery] = useState("");
  const [clinicOptions, setClinicOptions] = useState([]);
  const [clinic, setClinic] = useState(null);
  const [category, setCategory] = useState("general");
  const [priority, setPriority] = useState("medium");
  const [subject, setSubject] = useState("");

  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");
  const Toast = useToast();
  const auth = localStorage.getItem("token");
  const authHeaders = { headers: { Authorization: `Bearer ${auth}` } };

  const load = async (currentStatus, currentPage, currentSearch) => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/tickets`, {
        ...authHeaders,
        params: { status: currentStatus, page: currentPage, limit: rowsPerPage, search: currentSearch || undefined },
      });
      setTickets(res.data.data || []);
      setTotalCount(res.data.totalCount || 0);
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not load tickets.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const timeout = setTimeout(() => {
      if (!cancelled) load(status, page, search);
    }, search ? 400 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, page, search]);

  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  const toggleOpen = async (id) => {
    if (openId === id) {
      setOpenId("");
      setDetail(null);
      return;
    }
    setOpenId(id);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/tickets/${id}`, authHeaders);
      setDetail(res.data.data);
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not load this ticket.");
    } finally {
      setDetailLoading(false);
    }
  };

  const searchDoctors = async (keyword, clinicId = "") => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/tickets/doctors-search`, { ...authHeaders, params: { keyword, clinicId } });
      setDoctorOptions(res.data.doctors || []);
    } catch {
      setDoctorOptions([]);
    }
  };

  const onDoctorInput = (keyword) => {
    setDoctorQuery(keyword);
    setDoctor(null);
    if (!keyword || keyword.trim().length < 2) {
      setDoctorOptions([]);
      return;
    }
    searchDoctors(keyword, clinic?._id || "");
  };

  const pickDoctor = (d) => {
    const clinics = d.clinics || [];
    setDoctor(d);
    setDoctorQuery(clinics.length ? `${d.name} — ${clinics.map((c) => c.name).join(", ")}` : d.name);
    setDoctorOptions([]);
    setClinic(clinics.length === 1 ? clinics[0] : null);
    if (clinics.length === 1) setClinicQuery(clinics[0].name);
  };

  const onClinicInput = async (keyword) => {
    setClinicQuery(keyword);
    setClinic(null);
    if (!keyword || keyword.trim().length < 2) {
      setClinicOptions([]);
      return;
    }
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_HOST_URL}/ops/v1/getAllDoctorsAndClinic`, { ...authHeaders, params: { clinicKeyword: keyword } });
      setClinicOptions(res.data.data?.clinics || []);
    } catch {
      setClinicOptions([]);
    }
  };

  const pickClinic = (c) => {
    setClinic(c);
    setClinicQuery(c.name);
    setClinicOptions([]);
    setDoctor(null);
    setDoctorQuery("");
    searchDoctors("", c._id);
  };

  const createTicket = async () => {
    if (!doctor || !subject.trim()) return;
    setSaving(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/tickets`,
        { doctorId: doctor._id, subject: subject.trim(), category, priority },
        authHeaders
      );
      setToast(`${res.data.data.ticketId} created in the support dashboard.`);
      setFormOpen(false);
      setDoctor(null);
      setClinic(null);
      setDoctorQuery("");
      setClinicQuery("");
      setSubject("");
      setCategory("general");
      setPriority("medium");
      setStatus("Open");
      setPage(1);
      load("Open", 1, search);
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not create this ticket.");
    } finally {
      setSaving(false);
    }
  };

  const addComment = async (id) => {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/tickets/${id}/notes`, { text: draft }, authHeaders);
      setDraft("");
      const res = await axios.get(`${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/tickets/${id}`, authHeaders);
      setDetail(res.data.data);
      setToast("Comment added.");
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not add this comment.");
    } finally {
      setSaving(false);
    }
  };

  if (loading && tickets.length === 0) {
    return (
      <Box sx={{ ...engagementPageSx(t), position: "relative", minHeight: 300 }}>
        <PageLoader />
      </Box>
    );
  }

  return (
    <Box sx={engagementPageSx(t)}>
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-end", gap: "16px" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.015em", color: t.textPrimary }}>Support tickets</Typography>
          <Typography sx={{ fontSize: 15, color: t.textMuted }}>
            Tickets for your clinics. Support works them in the support dashboard; you raise, follow and comment here.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "flex-end" }}>
          <TextField
            size="small"
            placeholder="Search by doctor or clinic"
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            sx={{ width: 240, bgcolor: t.inputBg }}
          />
          <Box component="button" type="button" onClick={() => setFormOpen((o) => !o)} sx={engagementButtonSx(t, "solid")}>
            {formOpen ? "Close" : "Raise ticket"}
          </Box>
        </Box>
      </Box>

      {toast && (
        <Box role="status" sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "12px", padding: "14px 18px", borderRadius: "12px", bgcolor: t.tone.ok.bg, color: t.tone.ok.color, fontSize: 15, fontWeight: 600 }}>
          <span>{toast}</span>
          <Box component="button" type="button" onClick={() => setToast("")} sx={{ minHeight: 44, padding: "0 14px", border: `1px solid ${t.tone.ok.color}`, borderRadius: "8px", bgcolor: "transparent", color: t.tone.ok.color, font: "inherit", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
            Dismiss
          </Box>
        </Box>
      )}

      {formOpen && (
        <EngagementCard t={t} sx={{ border: `2px solid ${t.accent}` }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Raise ticket</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
            <Box sx={{ flex: "1 1 220px", display: "flex", flexDirection: "column", gap: "4px" }}>
              <TextField label="Doctor" placeholder="Search doctor by name" value={doctorQuery} onChange={(e) => onDoctorInput(e.target.value)} sx={{ bgcolor: t.inputBg }} />
              {!doctor && doctorOptions.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  {doctorOptions.map((o) => (
                    <Box key={o._id} onClick={() => pickDoctor(o)} sx={{ cursor: "pointer", padding: "6px 10px", borderRadius: "8px", bgcolor: t.hoverBg, fontSize: 14 }}>
                      {o.name}{o.clinics?.length ? ` — ${o.clinics.map((c) => c.name).join(", ")}` : ""}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            <Box sx={{ flex: "1 1 220px", display: "flex", flexDirection: "column", gap: "4px" }}>
              <TextField label="Clinic" placeholder="Search clinic by name" value={clinicQuery} onChange={(e) => onClinicInput(e.target.value)} sx={{ bgcolor: t.inputBg }} />
              {!clinic && clinicOptions.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  {clinicOptions.map((c) => (
                    <Box key={c._id} onClick={() => pickClinic(c)} sx={{ cursor: "pointer", padding: "6px 10px", borderRadius: "8px", bgcolor: t.hoverBg, fontSize: 14 }}>
                      {c.name}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            <Select size="small" value={category} onChange={(e) => setCategory(e.target.value)} sx={{ height: 44, minWidth: 140, bgcolor: t.inputBg }}>
              {CATEGORIES.map((c) => (
                <MenuItem key={c} value={c} sx={{ textTransform: "capitalize" }}>{c}</MenuItem>
              ))}
            </Select>
            <Select size="small" value={priority} onChange={(e) => setPriority(e.target.value)} sx={{ height: 44, minWidth: 140, bgcolor: t.inputBg }}>
              {PRIORITIES.map((p) => (
                <MenuItem key={p} value={p} sx={{ textTransform: "capitalize" }}>{p}</MenuItem>
              ))}
            </Select>
          </Box>
          <TextField label="What the clinic said" value={subject} onChange={(e) => setSubject(e.target.value)} sx={{ bgcolor: t.inputBg }} />
          <Box sx={{ display: "flex", gap: "10px" }}>
            <Box component="button" type="button" disabled={saving || !doctor || !subject.trim()} onClick={createTicket} sx={{ ...engagementButtonSx(t, "solid"), opacity: saving || !doctor || !subject.trim() ? 0.6 : 1 }}>
              {saving ? "Creating…" : "Create ticket"}
            </Box>
            <Box component="button" type="button" onClick={() => setFormOpen(false)} sx={{ ...engagementButtonSx(t), border: 0 }}>Cancel</Box>
          </Box>
        </EngagementCard>
      )}

      <Box role="group" aria-label="Status" sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
        {STATUS_CHIPS.map((s) => (
          <Box key={s} component="button" type="button" onClick={() => { setPage(1); setStatus(s); }} sx={engagementChipSx(t, status === s)}>{s}</Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "1px", bgcolor: t.border, border: `1px solid ${t.border}`, borderRadius: "12px", overflow: "hidden" }}>
        {tickets.length === 0 && <Box sx={{ bgcolor: t.cardBg, padding: "18px", fontSize: 14, color: t.textMuted }}>No tickets with this status.</Box>}
        {tickets.map((tk) => (
          <Box key={tk.id} sx={{ bgcolor: t.cardBg, padding: "14px 18px", display: "flex", flexDirection: "column", gap: "12px" }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "14px" }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, width: 90 }}>{tk.ticketId}</Typography>
              <Box sx={{ flex: "1 1 280px", minWidth: 0, display: "flex", flexDirection: "column", gap: "3px" }}>
                <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{tk.subject}</Typography>
                <Typography
                  component={tk.doctorId ? Link : "span"}
                  to={tk.doctorId ? `/dashboard/engagement/clinics/${tk.doctorId}` : undefined}
                  sx={{ fontSize: 13, color: t.accent, textDecoration: "none", fontWeight: 600 }}
                >
                  {tk.who}
                </Typography>
              </Box>
              <EngagementPill t={t} tone="mute">{tk.category}</EngagementPill>
              <EngagementPill t={t} tone={priorityTone(tk.priority)}>{tk.priority}</EngagementPill>
              <EngagementPill t={t} tone={statusTone(tk.status)}>{tk.status}</EngagementPill>
              <Typography sx={{ fontSize: 13, color: t.textSecondary, width: 170 }}>{tk.ageLabel}</Typography>
              <Box component="button" type="button" onClick={() => toggleOpen(tk.id)} sx={engagementButtonSx(t)}>
                {openId === tk.id ? "Close" : "View"}
              </Box>
            </Box>
            {openId === tk.id && (
              <Box sx={{ padding: "14px", borderRadius: "10px", bgcolor: t.hoverBg, display: "flex", flexDirection: "column", gap: "10px" }}>
                {detailLoading && <Typography sx={{ fontSize: 14, color: t.textMuted }}>Loading…</Typography>}
                {!detailLoading && detail && (
                  <>
                    {detail.description && <Typography sx={{ fontSize: 14 }}>{detail.description}</Typography>}
                    {detail.notes.length === 0 && <Typography sx={{ fontSize: 13, color: t.textMuted }}>No internal comments yet.</Typography>}
                    {detail.notes.map((n, i) => (
                      <Box key={i} sx={{ display: "flex", flexDirection: "column", gap: "2px", padding: "10px 12px", bgcolor: t.cardBg, borderRadius: "8px" }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{n.by}, {formatDateTime(n.createdAt)}</Typography>
                        <Typography sx={{ fontSize: 14 }}>{n.text}</Typography>
                      </Box>
                    ))}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                      <TextField
                        placeholder="Internal comment, never sent to the clinic"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        sx={{ bgcolor: t.inputBg, flex: "1 1 280px" }}
                      />
                      <Box component="button" type="button" disabled={saving || !draft.trim()} onClick={() => addComment(tk.id)} sx={{ ...engagementButtonSx(t), opacity: saving || !draft.trim() ? 0.6 : 1 }}>
                        Add comment
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
        <Typography sx={{ fontSize: 13, color: t.textMuted }}>Page {page} of {totalPages}</Typography>
        <Box sx={{ display: "flex", gap: "8px" }}>
          <Box
            component="button"
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, color: page <= 1 ? t.disabledText : t.textPrimary, cursor: page <= 1 ? "not-allowed" : "pointer" }}
          >
            Previous
          </Box>
          <Box
            component="button"
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, color: page >= totalPages ? t.disabledText : t.textPrimary, cursor: page >= totalPages ? "not-allowed" : "pointer" }}
          >
            Next
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EngagementTickets;
